import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import Button from 'components/common/Button';
import IconArrowTopRight from 'components/icons/IconArrowTopRight';
import Tooltip from 'components/common/Tooltip';

import selectInsurance from 'utils/selectInsurance';
import patientDobToAge from 'utils/patientDobToAge';
import getPatientFullAddress from 'utils/print/getPatientFullAddress';

import pxToRem from 'styles/pxToRem';

import primaryLanguages from 'config/primaryLanguages';

import PatientDetailsBarActions from './PatientDetailsBarActions';
import PatientDetailsBarTabs from './PatientDetailsBarTabs';

const styles = theme => ({
  root: {
    width: 'auto',
    minWidth: 1110,
    right: 0,
    padding: '16px 0 0',
     [theme.breakpoints.down('sm')]: {
      minWidth: 0,
    },
  },
  rootVariantCharts: {
    background: theme.palette.common.white,
  },
  rootVariantOrder: {
    position: 'absolute',
    top: 0,
    left: 0,
    paddingTop: 8,
    background: theme.palette.primary.A200,
  },
  container: {
    position: 'relative',
    width: 'auto',
    height: 46,
    margin: '0 16px 0',
    [theme.breakpoints.down('sm')]: {
      height: 91,
      margin: '0 8px 0',
    },
  },
  containerVariantCharts: {
    paddingLeft: 20,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
    },
  },
  containerVariantOrder: {
    minHeight: 64,
  },
  typographyTitle: {
    height: 24,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'calc(100% - 160px)',
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: 'calc(100% - 180px)',
    },
  },
  typographyPRN: {
    marginTop: 5,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  typographyCaption: {
    marginTop: 5,
    [theme.breakpoints.down('sm')]: {
      marginTop: -8,
    },
  },
  typographyBody: {
    marginTop: 3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  iconArrowTopRight: {
    width: 10,
    height: 10,
    marginRight: 10,
  },
  gridContainer: {
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      flexWrap: 'wrap',
    },
  },
  gridItemName: {
    flexShrink: 1,
    minWidth: 0,
    '*::-ms-backdrop, &': {
      flexBasis: '20%',
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      width: '100%',
      flexBasis: '100%',
      flexShrink: 0,
    },
  },
  gridItemActions: {
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      top: 0,
      right: 0,
    },
  },
  gridItemDesktopOnly: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  gridItem: {
    maxWidth: '20%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '40%',
    },
  },
  gridItemGender: {
    [theme.breakpoints.down('sm')]: {
      maxWidth: '20%',
    },
  },
  gridItemAddress: {
    maxWidth: '28%',
  },
  statusIcon: {
    display: 'inline-block',
    width: 10,
    height: 10,
    marginRight: 4,
    borderRadius: '50%',
    overflow: 'hidden',
    textIndent: '100%',
    cursor: 'pointer',
  },
  statusIconActive: {
    backgroundColor: theme.palette.active[500],
  },
  statusIconInactive: {
    backgroundColor: theme.palette.validation[500],
  },
  typographyValue: {
    color: theme.palette.text.primary,
  },
  buttonChange: {
    marginTop: 5,
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
    },
  },
  tooltipLanguage: {
    marginLeft: 4,
    fontSize: pxToRem(16),
    verticalAlign: 'bottom',
  },
  iconPrimaryLanguage: {
    width: 16,
    height: 16,
    verticalAlign: 'top',
  },
  gridItemChange: {
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      top: 0,
      right: 0,
    },
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  }
});

const PatientDetailsBar = ({
  variant,
  activeSection,
  onTabChange,
  onEditPatientInformationButtonClick,
  patient,
  business,
  classes,
}) => {
  const IconPrimaryLanguage = patient['Primary Language']
    ? primaryLanguages.find(item => item.code === patient['Primary Language']).icon
    : null;
  return (
    <AppBar
      elevation={variant === 'charts' ? 2 : 0}
      color="primary"
      // position={variant === 'charts' ? 'fixed' : 'absolute'}
      position="static"
      className={classNames(classes.root, {
        [classes.rootVariantCharts]: variant === 'charts',
        [classes.rootVariantOrder]: variant !== 'charts',
      })}
    >
      <div
        className={classNames(classes.container, {
          [classes.containerVariantCharts]: variant === 'charts',
          [classes.containerVariantOrder]: variant !== 'charts',
        })}
      >
        <Grid
          className={classes.gridContainer}
          container
          justify="space-between"
          spacing={16}
          wrap="nowrap"
        >
          <Grid item className={classes.gridItemName}>
            <Typography
              className={classes.typographyTitle}
              variant="title"
            >
              {patient['Last Name']}
              {`, ${patient['First Name']}`}
              {patient['Middle Name'] && ` ${patient['Middle Name']}`}
            </Typography>
            <Typography className={classes.typographyPRN} variant="caption">
              <Tooltip
                content={
                  <Typography>
                    {patient['Patient Status'] === true && 'Active'}
                    {(patient['Patient Status'] === false && patient['Date of Death']) && `Inactive, DOD: ${patient['Date of Death']}`}
                    {(patient['Patient Status'] === false && !patient['Date of Death']) && 'Inactive'}
                  </Typography>
                }
                placement="top"
              >
                <div
                  className={classNames(classes.statusIcon, {
                    [classes.statusIconActive]: patient['Patient Status'] === true,
                    [classes.statusIconInactive]: patient['Patient Status'] === false,
                  })}
                />
              </Tooltip>
              PRN:
              <span className={classes.typographyValue}>
                {patient['Record Number']}
              </span>
              {patient['Social Security Number'] && (
                <Fragment>
                  {' | '}
                  SSN:
                  <span className={classes.typographyValue}>
                    ***-**-
                    {patient['Social Security Number'].substr(-4)}
                  </span>
                </Fragment>
              )}
            </Typography>
          </Grid>
          <Grid className={classes.gridItem} item>
            <Typography className={classes.typographyCaption} variant="caption">
              DOB, {patient['Date of Death'] ? 'DOD' : 'Age'}
            </Typography>
            <Typography className={classes.typographyBody} variant="body1">
              <span
                onDoubleClick={(event) => {
                  event.stopPropagation();
                  const range = document.createRange();
                  range.selectNodeContents(event.currentTarget);
                  const selection = window.getSelection();
                  selection.removeAllRanges();
                  selection.addRange(range);
                }}
              >
                {patient['Date of Birth']}
              </span>
              <span>
                ,{' '}
                {patient['Date of Death'] ? patient['Date of Death'] : patientDobToAge(patient['Date of Birth'])}
              </span>
            </Typography>
          </Grid>
          <Grid
            className={classNames(classes.gridItem, classes.gridItemGender)}
            item
          >
            <Typography className={classes.typographyCaption} variant="caption">
              Gender
            </Typography>
            <Typography className={classes.typographyBody} variant="body1">
              {patient.Gender}
            </Typography>
          </Grid>
          <Grid
            className={classNames(classes.gridItem, {
              [classes.gridItemAddress]: variant !== 'charts',
            })}
            item
          >
            <Typography className={classes.typographyCaption} variant="caption">
              {variant === 'charts' ? (
                'Insurance'
              ) : (
                'Address'
              )}
            </Typography>
            <Typography className={classes.typographyBody} variant="body1">
              {variant === 'charts' && patient['Insurance Information'].filter(item => item['Insurance Type'] !== 'Self').length > 0
                && selectInsurance(patient['Insurance Information'])['Insurance Info']['Payer Name']
              }
              {variant === 'charts' && (patient['Insurance Information'].length === 0
                || (patient['Insurance Information'].length === 1
                  && patient['Insurance Information'][0]['Insurance Type'] === 'Self')) &&
                'Self Payer'
              }
              {variant !== 'charts' &&
                getPatientFullAddress({ patient })
              }
            </Typography>
          </Grid>
          {patient['Contact Information']['Primary Phone'] &&
            <Grid
              className={classNames(classes.gridItem, classes.gridItemDesktopOnly)}
              item
            >
              <Typography className={classes.typographyCaption} variant="caption">
                Contact
                {patient['Primary Language'] &&
                  <Tooltip
                    className={classes.tooltipLanguage}
                    content={
                      <Typography>
                        Primary Language: {primaryLanguages.find(item => item.code === patient['Primary Language']).name}
                      </Typography>
                    }
                    placement="top"
                  >
                    <IconPrimaryLanguage className={classes.iconPrimaryLanguage} />
                  </Tooltip>
                }
              </Typography>
              <Typography className={classes.typographyBody} variant="body1">
                {patient['Contact Information']['Primary Phone']}
              </Typography>
            </Grid>
          }
          {patient['Referral Source'][0] &&
            <Grid
              className={classNames(classes.gridItem, classes.gridItemDesktopOnly)}
              item
            >
              <Typography className={classes.typographyCaption} variant="caption">
                Referral
              </Typography>
              <Typography className={classes.typographyBody} variant="body1">
                {patient['Referral Source'][0]['Name of source']}
              </Typography>
            </Grid>
          }
          {patient['Sales Representative'][0] &&
            <Grid
              className={classNames(classes.gridItem, classes.gridItemDesktopOnly)}
              item
            >
              <Typography className={classes.typographyCaption} variant="caption">
                Sales
              </Typography>
              <Typography className={classes.typographyBody} variant="body1">
                {patient['Sales Representative'][0]['First Name']}
                {' '}
                {patient['Sales Representative'][0]['Last Name']}
              </Typography>
            </Grid>
          }
          {variant === 'charts' &&
            <Grid
              className={classes.gridItemActions}
              item
            >
              <PatientDetailsBarActions
                patient={patient}
                business={business}
                onEditPatientInformationButtonClick={onEditPatientInformationButtonClick}
              />
            </Grid>
          }
          {variant === 'order-edit' &&
            <Grid item className={classes.gridItemChange}>
              <Button
                className={classes.buttonChange}
                variant="raised"
                size="md"
                href={`/charts/${patient['Record Number']}`}
              >
                <IconArrowTopRight className={classes.iconArrowTopRight} />
                Change
              </Button>
            </Grid>
          }
        </Grid>
      </div>
      {variant === 'charts' &&
        <PatientDetailsBarTabs
          patient={patient}
          value={activeSection}
          {...onTabChange ? { onChange: onTabChange } : {}}
        />
      }
    </AppBar>
  );
};

PatientDetailsBar.propTypes = {
  variant: PropTypes.oneOf([
    'charts',
    'order-create',
    'order-edit',
  ]),
  patient: PropTypes.shape({
    'First Name': PropTypes.string.isRequired,
    'Last Name': PropTypes.string.isRequired,
    'Middle Name': PropTypes.string,
    'Record Number': PropTypes.number.isRequired,
    'Date of Birth': PropTypes.string.isRequired,
    'Referral Source': PropTypes.arrayOf(PropTypes.shape({
      'Name of source': PropTypes.string,
    })),
    'Insurance Information': PropTypes.arrayOf(PropTypes.shape({
      'Insurance Status': PropTypes.string,
      'Payer Name': PropTypes.string,
    })),
    'Contact Information': PropTypes.shape({
      'Primary Phone': PropTypes.string,
    }),
    Gender: PropTypes.string.isRequired,
  }).isRequired,
  activeSection: PropTypes.oneOf(['summary', 'profile', 'history']),
  onTabChange: PropTypes.func,
  onEditPatientInformationButtonClick: PropTypes.func,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    gridItem: PropTypes.string.isRequired,
    gridItemAddress: PropTypes.string.isRequired,
    typographyTitle: PropTypes.string.isRequired,
    typographyCaption: PropTypes.string.isRequired,
    typographyBody: PropTypes.string.isRequired,
    iconArrowTopRight: PropTypes.string.isRequired,
    statusIcon: PropTypes.string.isRequired,
    statusIconActive: PropTypes.string.isRequired,
    statusIconInactive: PropTypes.string.isRequired,
  }).isRequired,
};

PatientDetailsBar.defaultProps = {
  variant: 'charts',
  'Middle Name': null,
  'Referral Source': null,
  'Insurance Information': null,
  'Contact Information': null,
  activeSection: 'summary',
  onTabChange: null,
  onEditPatientInformationButtonClick: null,
  business: {},
};

export default withStyles(styles)(PatientDetailsBar);



// WEBPACK FOOTER //
// ./src/components/patient/PatientDetailsBar/PatientDetailsBar.js